import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { isSuperAdmin, isCompanyAdmin, getEffectiveRole, ROLES } from '../services/users';

// User interface based on API response structure. Field names match the JSON
// the API actually returns (snake_case). The API's authService.sanitizeUser
// passes through the DB row with `password_hash` stripped, so `company_id`
// and `company_name` arrive snake_case on the wire — this interface reflects
// that. Earlier versions of this type used `companyId` (camelCase) and the
// codebase silently read the missing field via runtime duck-typing; that's
// fixed here.
export interface User {
  id: number;
  email: string;
  name: string;
  role?: string;
  role_new?: string;
  effectiveRole?: string;
  company_id?: number;
  company_name?: string;
  // Marketplace seller accounts (role manufacturer_admin) carry these instead.
  manufacturer_id?: number | null;
  manufacturer_name?: string | null;
  is_active: boolean;
  // Two-factor authentication (TOTP). mfa_enabled_at is null until enrolled.
  mfa_enabled?: boolean;
  mfa_enabled_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

// AuthContext value interface
export interface SignupData {
  accountType: 'software' | 'hardware';
  organizationName: string;
  name: string;
  email: string;
  password: string;
  // Turnstile token when the API advertises a captcha (GET /auth/signup-config).
  captchaToken?: string;
}

// What /auth/login (and /auth/verify-email) return when the password was
// right but a second factor stands between the user and a session. The
// mfaToken is short-lived and only spendable on the /auth/mfa/* endpoints.
export interface MfaChallenge {
  mfaRequired?: boolean;
  mfaSetupRequired?: boolean;
  mfaToken: string;
}

export interface MfaSetup {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

export type LoginResult = { user: User } | MfaChallenge;

export function isMfaChallenge(result: LoginResult): result is MfaChallenge {
  return typeof (result as MfaChallenge).mfaToken === 'string';
}

interface SessionPayload {
  token: string;
  user: User;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  // Second step of sign-in for enrolled users: a TOTP or recovery code.
  completeMfa: (mfaToken: string, code: string) => Promise<{ user: User; usedRecoveryCode?: boolean; recoveryCodesRemaining?: number }>;
  // Enrollment during sign-in for users who have no authenticator yet.
  startMfaSetup: (mfaToken: string) => Promise<MfaSetup>;
  finishMfaSetup: (mfaToken: string, code: string) => Promise<{ user: User; recoveryCodes: string[] }>;
  signup: (data: SignupData) => Promise<{ verificationRequired: true; email: string }>;
  // Resolves null when the address was verified but MFA setup is still
  // needed — the caller sends the user to sign in.
  verifyEmail: (token: string) => Promise<User | null>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  // Role helpers
  isSuperAdmin: () => boolean;
  isCompanyAdmin: () => boolean;
  getEffectiveRole: () => string;
  hasRole: (role: string) => boolean;
  // Permission checks
  canManageUsers: () => boolean;
  canManageCompanies: () => boolean;
  canManageBilling: () => boolean;
  canManageGateways: () => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  async function fetchUser() {
    try {
      const response = await api.get<User>('/users/me');
      setUser(response.data);
    } catch (err) {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  }

  function applySession({ token, user: sessionUser }: SessionPayload): User {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(sessionUser);
    return sessionUser;
  }

  async function login(email: string, password: string): Promise<LoginResult> {
    const response = await api.post<SessionPayload | MfaChallenge>('/auth/login', { email, password });
    const data = response.data;
    if ('mfaToken' in data) {
      return data;
    }
    return { user: applySession(data) };
  }

  async function completeMfa(mfaToken: string, code: string) {
    const response = await api.post<SessionPayload & { usedRecoveryCode?: boolean; recoveryCodesRemaining?: number }>(
      '/auth/mfa/verify',
      { mfaToken, code },
    );
    const { usedRecoveryCode, recoveryCodesRemaining } = response.data;
    return { user: applySession(response.data), usedRecoveryCode, recoveryCodesRemaining };
  }

  async function startMfaSetup(mfaToken: string): Promise<MfaSetup> {
    const response = await api.post<MfaSetup>('/auth/mfa/setup', { mfaToken });
    return response.data;
  }

  async function finishMfaSetup(mfaToken: string, code: string) {
    const response = await api.post<SessionPayload & { recoveryCodes: string[] }>('/auth/mfa/enable', { mfaToken, code });
    return { user: applySession(response.data), recoveryCodes: response.data.recoveryCodes };
  }

  // Self-serve signup: creates the organization (software provider = company,
  // hardware provider = manufacturer) plus its first admin. No session yet —
  // the account activates via the emailed verification link.
  async function signup(data: SignupData): Promise<{ verificationRequired: true; email: string }> {
    const response = await api.post<{ verificationRequired: true; email: string }>('/auth/signup', data);
    return response.data;
  }

  // Complete verification from the emailed link — the token proves ownership,
  // so a successful verify also signs the user in (unless MFA setup is still
  // owed, in which case the sign-in page walks them through it).
  async function verifyEmail(verificationToken: string): Promise<User | null> {
    const response = await api.post<SessionPayload | MfaChallenge>('/auth/verify-email', { token: verificationToken });
    const data = response.data;
    if ('mfaToken' in data) {
      return null;
    }
    return applySession(data);
  }

  function logout() {
    // Best-effort: clears the HttpOnly session cookie the OAuth /authorize
    // endpoint reads, so "Logout" ends that session too, not just the SPA's.
    api.post('/auth/logout').catch(() => undefined);
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }

  // Refresh user data (useful after profile updates)
  async function refreshUser() {
    await fetchUser();
  }

  const value: AuthContextValue = {
    user,
    loading,
    login,
    completeMfa,
    startMfaSetup,
    finishMfaSetup,
    signup,
    verifyEmail,
    logout,
    refreshUser,
    // Role helpers
    isSuperAdmin: () => isSuperAdmin(user),
    isCompanyAdmin: () => isCompanyAdmin(user),
    getEffectiveRole: () => getEffectiveRole(user),
    hasRole: (role: string) => getEffectiveRole(user) === role,
    // Permission checks
    canManageUsers: () => isCompanyAdmin(user),
    canManageCompanies: () => isSuperAdmin(user),
    canManageBilling: () => isSuperAdmin(user),
    canManageGateways: () => isCompanyAdmin(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Re-export role constants for convenience
export { ROLES };
